import SlideOver from "../../../common/components/SlideOver/SlideOver";
import { useTranslation } from "react-i18next";
import { useEffect } from "react";
import type { RootState } from "../../../common/state/store";
import type { IElevenlabsVoice } from "../state/gallerySlice";
import {
  currentMediaSelector,
  updateMediaDetail,
  updateAvatarVoices,
  setUploadedOldFile,
  transcribePdf,
  getCurrentUploadsStateList,
  getElevenlabsVoices,
} from "../state/gallerySlice";
import { MdDeleteOutline } from "react-icons/md";
import type { IMedia } from "../../../model/unityObject";
import { MediaViewer } from "./MediaViewer";
import { useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../common/state/hooks";
import { useForm } from "react-hook-form";
import {
  closeConfirmationModal,
  openConfirmationModal,
} from "../../../common/state/slice/modal/modalSlice";
import { IsLoading } from "../../../common/components/AppState/IsLoading";
import { Input, Dropdown } from "../../../common/components/Input";
import { useLocation } from "react-router-dom";
import { prettifyFileSizeInBytes } from "../../../common/util/prettifyFileSizeInBytes";
import { languageConfig } from "../../../common/util/langConfig";
import type { ILanguage } from "../../../model/model";
import { getCurrentOrganization, getCurrentUser } from "../../profile/state/profileSlice";
import Media360TagDropdown from "./Media360TagDropdown";
import { canMediaBeReplaced } from "../util/canMediaBeReplaced";
import ModalConfirmationToDeleteMedia from "src/common/components/Modal/ModalConfirmationToDeleteMedia";
import ModalConfirmationToReplaceMedia from "src/common/components/Modal/ModalConfirmationToReplaceMedia";
import { MdOutlineFileUpload } from "react-icons/md";
import hasMediaBeenGeneratedByAi from "src/common/util/HasMediaBeenGeneratedByAi";
import getMediaMetadata from "src/common/util/getMediaMetadata";
import PdfTranscription from "./PdfTranscription";
import { FloatingTooltip } from "../../../common/components/Tooltip/FloatingTooltip";
import Spinner from "src/common/components/Loader/Spinner";
import { useEnsureElevenlabsVoices } from "src/features/gallery/useEnsureElevenlabsVoices";

const CharacterCounter = ({
  currentLength,
  maxLength,
}: {
  currentLength: number;
  maxLength: number;
}) => {
  const { t } = useTranslation();
  const isNearLimit = currentLength >= maxLength - 5;
  const isAtLimit = currentLength >= maxLength;

  return (
    <div
      className={`text-sm mt-1 ${
        isAtLimit ? "text-red-600" : isNearLimit ? "text-yellow-600" : "text-gray-500"
      }`}
    >
      {currentLength}/{maxLength} {t("general.characters") || "characters"}
    </div>
  );
};

interface IMediaDetail {
  media: IMedia;
  large: boolean;
  open: boolean;
  onClose: () => void;
}

export const MEDIA_TAGS_COMPONENT_ID = "tagSelector";
export const MEDIA_TAGS_CHECK_BEFORE_DELETE_ID = "tagSelectorCheckBeforeDelete";
export const MEDIA_NEW_DATA_FOR_UPDATE = "mediaEditForUpdateData";

export const MediaDetailContainer = ({
  open,
  onClose,
  large,
}: {
  open: boolean;
  onClose: () => void;
  large: boolean;
}) => {
  const media = useAppSelector((state: RootState) => currentMediaSelector(state));
  return <>{media && <MediaDetail open={open} onClose={onClose} large={large} media={media} />}</>;
};

export const MediaDetail = ({ open, onClose, large, media }: IMediaDetail) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const location = useLocation();

  const currentUser = useAppSelector(getCurrentUser);
  const currentLocale = languageConfig((currentUser?.preferred_language as ILanguage)?.name);

  const mediaMetadata = getMediaMetadata(media);
  const hasBeenGeneratedByAi = hasMediaBeenGeneratedByAi(media);
  const currentOrganization = useAppSelector(getCurrentOrganization);
  const canReplace = canMediaBeReplaced(media, currentOrganization?.id);

  // To avoid painful issues keeping the store in sync, we disable the media deletion when not in the dedicated gallery
  const path = location.pathname.split("/");
  const shouldHideSuppressButton = !(
    path.length > 1 && location.pathname.split("/")[1] === "medias"
  );

  const [areButtonsDisabled, setAreButtonsDisabled] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    dispatch(closeConfirmationModal("deleteMediaConfirmationFromList"));
  }, []);

  const onDeleteMedia = () => {
    setAreButtonsDisabled(true);
    dispatch(openConfirmationModal("deleteMedia"));
    dispatch(setUploadedOldFile(undefined));
    setAreButtonsDisabled(false);
  };

  const onReplaceMedia = () => {
    dispatch(openConfirmationModal("replaceMedia"));
  };

  type mediaValues = {
    name: string;
    media_360_tag: string;
  };
  const {
    register: registerNewMediaDataEdited,
    reset: resetMediaData,
    watch,
    formState: { isDirty, errors },
    getValues,
  } = useForm<mediaValues>({
    defaultValues: {
      name: media.name,
    },
  });
  useEffect(() => {
    resetMediaData({
      name: media.name,
    });
  }, [open]);

  const onSubmitMediaEdit = async (
    name: string,
    media360Tag: string,
    voices?: { voiceFr: VoiceSelection; voiceEn: VoiceSelection; voiceEs: VoiceSelection },
  ) => {
    if (isSaving) return;

    const mediaDataEdited = {
      ...media,
      name: name,
      media_360_tag: media360Tag,
    };
    if (mediaDataEdited.name !== "") {
      setIsSaving(true);
      try {
        await dispatch(
          updateMediaDetail({
            media: mediaDataEdited,
            componentId: MEDIA_NEW_DATA_FOR_UPDATE,
          }),
        );

        if (voices && media.type === "avatar") {
          const voicesHaveChanged =
            voices.voiceFr.voiceId !== getInitialVoice("fr").voiceId ||
            voices.voiceEn.voiceId !== getInitialVoice("en").voiceId ||
            voices.voiceEs.voiceId !== getInitialVoice("es").voiceId;

          if (voicesHaveChanged) {
            await dispatch(
              updateAvatarVoices({
                mediaId: media.id,
                voices: [
                  {
                    voiceId: voices.voiceFr.voiceId,
                    lang: "fr",
                    gender: voices.voiceFr.gender || "unknown",
                    prettyVoiceName: voices.voiceFr.name || "unknown",
                    tts_provider: voices.voiceFr.ttsProvider,
                  },
                  {
                    voiceId: voices.voiceEn.voiceId,
                    lang: "en",
                    gender: voices.voiceEn.gender || "unknown",
                    prettyVoiceName: voices.voiceEn.name || "unknown",
                    tts_provider: voices.voiceEn.ttsProvider,
                  },
                  {
                    voiceId: voices.voiceEs.voiceId,
                    lang: "es",
                    gender: voices.voiceEs.gender || "unknown",
                    prettyVoiceName: voices.voiceEs.name || "unknown",
                    tts_provider: voices.voiceEs.ttsProvider,
                  },
                ],
                componentId: MEDIA_NEW_DATA_FOR_UPDATE,
              }),
            );
          }
        }

        onClose();
        resetMediaData({
          name: name,
          media_360_tag: media360Tag,
        });
      } finally {
        setIsSaving(false);
      }
    }
  };

  type VoiceSelection = {
    voiceId: string;
    ttsProvider: string;
    name?: string;
    gender?: string;
  };

  const cancel = () => {
    onClose();

    resetMediaData({
      name: media.name,
      media_360_tag: media.media_360_tag,
    });
  };

  const currentUploads = useAppSelector(getCurrentUploadsStateList);
  const isTranscribing = currentUploads.some(
    (u) => u.s3name === media.filename && u.uploadState === "transcribing",
  );

  const elevenLabsVoices = useAppSelector(getElevenlabsVoices);
  useEnsureElevenlabsVoices(media.type === "avatar");
  const isAvatar = media.type === "avatar";

  const mapElevenlabsVoices = () => {
    return elevenLabsVoices.map((voice: IElevenlabsVoice) => {
      const localizedVoiceGender = t(`general.${voice.gender}`);
      return {
        ...voice,
        value: voice.id,
        optionText: `${voice.name} (${localizedVoiceGender})`,
        gender: voice.gender,
      };
    });
  };

  const fetchedVoices = mapElevenlabsVoices();
  const voiceOptionsFR = fetchedVoices.filter(
    (elt) => elt.language === "fr" || elt.tts_provider === "elevenLabs",
  );
  const voiceOptionsEN = fetchedVoices.filter(
    (elt) => elt.language === "en" || elt.tts_provider === "elevenLabs",
  );
  const voiceOptionsES = fetchedVoices.filter(
    (elt) => elt.language === "es" || elt.tts_provider === "elevenLabs",
  );

  const getInitialVoice = (lang: string): VoiceSelection => {
    if (isAvatar) {
      const metadataVoices = media.metadata?.avatarMetadata?.voices || [];
      const existingVoice = metadataVoices.find((v) => v.lang === lang);
      if (existingVoice) {
        return {
          voiceId: existingVoice.voiceId,
          ttsProvider: existingVoice.ttsProvider,
          name: existingVoice.prettyVoiceName,
          gender: existingVoice.gender,
        };
      }
    }
    return { voiceId: "", ttsProvider: "" };
  };

  const [voiceFr, setVoiceFr] = useState<VoiceSelection>(getInitialVoice("fr"));
  const [voiceEn, setVoiceEn] = useState<VoiceSelection>(getInitialVoice("en"));
  const [voiceEs, setVoiceEs] = useState<VoiceSelection>(getInitialVoice("es"));

  const avatarVoicesKey = isAvatar ? JSON.stringify(media.metadata?.avatarMetadata?.voices) : "";
  useEffect(() => {
    if (isAvatar) {
      setVoiceFr(getInitialVoice("fr"));
      setVoiceEn(getInitialVoice("en"));
      setVoiceEs(getInitialVoice("es"));
    }
  }, [media.id, avatarVoicesKey]);

  return (
    <SlideOver open={open} onClose={onClose} large={large}>
      <IsLoading componentId={MEDIA_TAGS_COMPONENT_ID} showSuccess={false} />
      <IsLoading componentId={MEDIA_TAGS_CHECK_BEFORE_DELETE_ID} showSuccess={false} />
      <IsLoading componentId={MEDIA_NEW_DATA_FOR_UPDATE} showSuccess={false} />
      <div className="flex w-full flex-col justify-between p-6 h-full">
        <div className="toto">
          <MediaViewer media={media} inSlideOver />
          <div className="my-3">
            <h1 className="text-sm font-medium">{media.name}</h1>

            {!(media.organization?.id?.toString() === "3") &&
              (media.created_at || media.createdAt) && (
                <h1 className="text-sm font-medium text-gray-500">
                  {t("pages.gallery.addedOn")}{" "}
                  {new Date(media.created_at || media.createdAt).toLocaleDateString(currentLocale, {
                    day: "numeric",
                    month: "long",
                    year: "numeric",
                  })}
                </h1>
              )}
            <h1 className="text-sm font-medium text-gray-500">
              {prettifyFileSizeInBytes(media.size_in_bytes as number, currentLocale)}
            </h1>
            {hasBeenGeneratedByAi && (
              <h1 className="text-sm font-medium text-gray-500 italic">
                {t("pages.gallery.generatedByAi")} ✨
              </h1>
            )}
          </div>

          {hasBeenGeneratedByAi && (
            <FloatingTooltip content={t("pages.gallery.initialPromptTooltip")}>
              <div className="mt-4 mb-2">
                <label
                  htmlFor="initialPrompt"
                  className="block text-lg font-medium text-gray-700 mb-2"
                >
                  {t("pages.gallery.initialPrompt")}
                </label>
                <textarea
                  className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm pb-0"
                  disabled
                  value={mediaMetadata?.initialPrompt || ""}
                  rows={4}
                />
              </div>
            </FloatingTooltip>
          )}
          {media.type === "pdf" &&
            (isTranscribing ? (
              <div className="my-4 w-full">
                <div className="relative w-full h-[48px]">
                  <span className="absolute left-1/2 top-1/2 transform -translate-x-1/2 translate-y-[22px] text-gray-500 text-sm italic">
                    {t("pages.gallery.transcribingInProgress")}
                  </span>
                  <Spinner relative />
                </div>
              </div>
            ) : (
              <PdfTranscription
                transcription={media.source_media_cache?.transcription}
                onRetry={() => {
                  void dispatch(transcribePdf({ mediaId: media.id }));
                }}
              />
            ))}

          <div className="flex pb-4">
            <div className="w-full">
              <form
                id="media-details-section"
                onSubmit={(e) => {
                  e.preventDefault();
                  const hasFormChanges =
                    isDirty ||
                    (isAvatar &&
                      (voiceFr.voiceId !== getInitialVoice("fr").voiceId ||
                        voiceEn.voiceId !== getInitialVoice("en").voiceId ||
                        voiceEs.voiceId !== getInitialVoice("es").voiceId));
                  if (
                    hasFormChanges &&
                    !errors.name &&
                    (watch("name") || "").trim() !== "" &&
                    (watch("name") || "").length <= 55
                  ) {
                    void onSubmitMediaEdit(
                      getValues("name"),
                      getValues("media_360_tag"),
                      isAvatar ? { voiceFr, voiceEn, voiceEs } : undefined,
                    );
                  }
                }}
              >
                <div className="mb-4">
                  <div className="block text-lg font-medium text-gray-700 mb-2">
                    {t("general.renameFile")}
                  </div>
                  <Input
                    type="text"
                    registerFct={() =>
                      registerNewMediaDataEdited("name", {
                        required: true,
                        maxLength: 55,
                      })
                    }
                    placeholder={t("general.fileName")}
                    required
                  />
                  <CharacterCounter currentLength={(watch("name") || "").length} maxLength={55} />
                  {errors.name?.type === "maxLength" && (
                    <span className="text-red-600 text-sm mt-2">
                      {t("alerts.maxCharacters") || "Maximum characters exceeded"}
                    </span>
                  )}
                </div>
                <Media360TagDropdown
                  mode="edit"
                  media={media}
                  registerFct={() =>
                    registerNewMediaDataEdited("media_360_tag", { required: true })
                  }
                />

                {isAvatar && voiceOptionsFR.length > 0 && (
                  <div className="mt-4">
                    <div className="block text-lg font-medium text-gray-700 mb-2">
                      {t("pages.gallery.generateVoicePanel.voice") || "Voices"}
                    </div>
                    <div className="flex flex-col gap-2">
                      <Dropdown
                        options={voiceOptionsFR}
                        label={t("pages.gallery.generateVoicePanel.voice") + " FR"}
                        labelFontClassName={"text-md mt-2"}
                        onChangeCb={(_value: string, index: number) => {
                          setVoiceFr({
                            voiceId: voiceOptionsFR[index].id,
                            ttsProvider: voiceOptionsFR[index].tts_provider,
                            name: voiceOptionsFR[index].name,
                            gender: voiceOptionsFR[index].gender,
                          });
                        }}
                        asDefaultValue
                        defaultValueIndex={Math.max(
                          voiceOptionsFR.findIndex((option) => option.value === voiceFr.voiceId),
                          0,
                        )}
                        disabled
                      />

                      <Dropdown
                        options={voiceOptionsEN}
                        label={t("pages.gallery.generateVoicePanel.voice") + " EN"}
                        labelFontClassName={"text-md mt-2"}
                        onChangeCb={(_value: string, index: number) => {
                          setVoiceEn({
                            voiceId: voiceOptionsEN[index].id,
                            ttsProvider: voiceOptionsEN[index].tts_provider,
                            name: voiceOptionsEN[index].name,
                            gender: voiceOptionsEN[index].gender,
                          });
                        }}
                        asDefaultValue
                        defaultValueIndex={Math.max(
                          voiceOptionsEN.findIndex((option) => option.value === voiceEn.voiceId),
                          0,
                        )}
                        disabled
                      />

                      <Dropdown
                        options={voiceOptionsES}
                        label={t("pages.gallery.generateVoicePanel.voice") + " ES"}
                        labelFontClassName={"text-md mt-2"}
                        onChangeCb={(_value: string, index: number) => {
                          setVoiceEs({
                            voiceId: voiceOptionsES[index].id,
                            ttsProvider: voiceOptionsES[index].tts_provider,
                            name: voiceOptionsES[index].name,
                            gender: voiceOptionsES[index].gender,
                          });
                        }}
                        asDefaultValue
                        defaultValueIndex={Math.max(
                          voiceOptionsES.findIndex((option) => option.value === voiceEs.voiceId),
                          0,
                        )}
                        disabled
                      />
                    </div>
                  </div>
                )}

                <div className="mr-2 mt-3 bottom-4 right-28 absolute">
                  <button
                    type="submit"
                    className="btn-primary-fill mt-2 -bottom-px -right-24 absolute"
                    disabled={
                      isSaving ||
                      (!isDirty &&
                        !(
                          isAvatar &&
                          (voiceFr.voiceId !== getInitialVoice("fr").voiceId ||
                            voiceEn.voiceId !== getInitialVoice("en").voiceId ||
                            voiceEs.voiceId !== getInitialVoice("es").voiceId)
                        )) ||
                      !!errors.name ||
                      (watch("name") || "").trim() === "" ||
                      (watch("name") || "").length > 55
                    }
                  >
                    {t("general.save")}
                  </button>
                  <div className="mt-3">
                    <button
                      type="button"
                      onClick={cancel}
                      className="btn-alternative-outline mt-2 absolute -bottom-px -left-80 ml-3.5"
                    >
                      {t("general.cancel")}
                    </button>
                  </div>
                </div>
              </form>
            </div>
            <div className="mt-3">
              <div className="flex flex-wrap justify-end gap-y-2 left-10 right-32 bottom-4 absolute pointer-events-none">
                {canReplace && (
                  <button
                    type="button"
                    className="flex mr-5 items-center btn-alternative-outline pointer-events-auto"
                    onClick={(e) => {
                      e.preventDefault();
                      onReplaceMedia();
                    }}
                    disabled={areButtonsDisabled}
                  >
                    <MdOutlineFileUpload size={20} />

                    {t("pages.gallery.replaceFile")}
                  </button>
                )}
                <button
                  type="button"
                  className={`flex mr-5 items-center btn-alternative-outline pointer-events-auto ${
                    shouldHideSuppressButton ? "hidden" : ""
                  }`}
                  onClick={(e) => {
                    e.preventDefault();
                    onDeleteMedia();
                  }}
                  disabled={areButtonsDisabled || shouldHideSuppressButton}
                >
                  <MdDeleteOutline size={20} />

                  {t("general.delete")}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <ModalConfirmationToDeleteMedia
        id={"deleteMedia"}
        idMedia={media.id}
        mediasToDelete={[media]}
      />
      {canReplace && <ModalConfirmationToReplaceMedia id={"replaceMedia"} media={media} />}
    </SlideOver>
  );
};
